import { SearchPage } from "@components/search/SearchPage"
import { SearchProvider } from "@components/search/SearchProvider"
import { AdminKeysPanel, useAdminKey } from "@components/search/AdminKeysPanel"
import { useAdminSession } from "@components/search/AdminSignIn"
import { adminKeys } from "./services/adminKeys"
import { SettingsPage } from "@components/search/SettingsPage"
import { LogOut, Settings } from "lucide-react"
import { useEffect, useState } from "react"
import { CURRENCY_OPTIONS } from "../utils/currency"
import { ReverseDemoService, type ReverseDemoStore, type StoreNotBootstrappedError } from "./services/ReverseDemoService"
import { SearchPostHogClient } from "./services/SearchPostHogClient"
import { TokenManager } from "./services/TokenManager"
import "@/index.css"

const ACTIVE_STORE_URL_KEY = "in5_search_active_store_url"
const ACTIVE_STORE_SLUG_KEY = "in5_search_active_store_slug"
const ACTIVE_EXTERNAL_SESSION_ID_KEY = "in5_search_external_session_id"

/**
 * Standalone Search App
 *
 * This is the root component for the standalone search application.
 * It handles authentication and provides the search interface.
 *
 * Features:
 * - JWT token management
 * - Automatic token refresh
 * - Error handling and retry
 * - Full responsive design
 */
export default function App() {
	const [isAuthenticated, setIsAuthenticated] = useState(false)
	const [authError, setAuthError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [organizationId, setOrganizationId] = useState<string>("default-org")
	const [currentView, setCurrentView] = useState<"search" | "settings">("search")
	const [storeUrl, setStoreUrl] = useState("")
	const [activeStoreUrl, setActiveStoreUrl] = useState<string>(() => sessionStorage.getItem(ACTIVE_STORE_URL_KEY) || "")
	const [activeStoreSlug, setActiveStoreSlug] = useState<string>(() => sessionStorage.getItem(ACTIVE_STORE_SLUG_KEY) || "")
	const [externalSessionId, setExternalSessionId] = useState<string>(
		() => sessionStorage.getItem(ACTIVE_EXTERNAL_SESSION_ID_KEY) || "",
	)
	const [stores, setStores] = useState<ReverseDemoStore[]>([])
	const [bootstrapProfileName, setBootstrapProfileName] = useState("reverse-demo")
	const [bootstrapRunExtractor, setBootstrapRunExtractor] = useState(true)
	const [bootstrapDefaultCurrency, setBootstrapDefaultCurrency] = useState("INR")
	const [bootstrapModalOpen, setBootstrapModalOpen] = useState(false)
	const [bootstrapPendingStoreUrl, setBootstrapPendingStoreUrl] = useState("")
	const [openActionsForStore, setOpenActionsForStore] = useState<string | null>(null)

	const reverseDemoService = ReverseDemoService.getInstance()
	const searchPostHog = SearchPostHogClient.getInstance()
	const tokenManager = TokenManager.getInstance()
	const enableAdmin = import.meta.env.VITE_ENABLE_REVERSE_DEMO_ADMIN === "true"
	const bootstrapSecret = useAdminKey("operator")
	// Operators either sign in (allowlisted in5 account) or use the operator key.
	const adminSession = useAdminSession()
	const canOperate = Boolean(bootstrapSecret) || adminSession.operator

	const buildActiveCorrelationProperties = () =>
		searchPostHog.buildCorrelationProperties({
			organizationId,
			externalSessionId,
			storeSlug: activeStoreSlug,
			storeUrl: activeStoreUrl,
		})

	useEffect(() => {
		searchPostHog.initialize()
		initializeAuthFromStoredToken()
	}, [])

	useEffect(() => {
		if (!isAuthenticated && enableAdmin) {
			handleRefreshStores(false)
		}
	}, [isAuthenticated, enableAdmin, canOperate])

	const initializeAuthFromStoredToken = async () => {
		try {
			setIsLoading(true)
			setAuthError(null)

			if (tokenManager.isWsTokenValid() && tokenManager.isBackendTokenValid()) {
				const token = await tokenManager.getToken()
				const orgId = decodeOrgId(token)
				setOrganizationId(orgId)
				setIsAuthenticated(true)
				tokenManager.stopRefresh()
			}
		} catch (error) {
			console.error("[App] Authentication error:", error)
			setAuthError(error instanceof Error ? error.message : "Authentication failed")
			setIsAuthenticated(false)
		} finally {
			setIsLoading(false)
		}
	}

	const decodeOrgId = (token: string) => {
		try {
			const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")
			const payload = JSON.parse(atob(base64))
			return payload.org_id || import.meta.env.VITE_ORGANIZATION_ID || "default-org"
		} catch {
			return import.meta.env.VITE_ORGANIZATION_ID || "default-org"
		}
	}

	const enterStore = async (inputStoreUrl?: string) => {
		const targetStoreUrl = (inputStoreUrl || storeUrl).trim()
		if (!targetStoreUrl) {
			setAuthError("Please enter a store URL")
			return
		}

		searchPostHog.capture("search_store_enter_submitted", {
			...searchPostHog.buildCorrelationProperties({ organizationId, storeUrl: targetStoreUrl }),
			has_input_store_url: Boolean(inputStoreUrl),
		})

		try {
			setIsLoading(true)
			setAuthError(null)

			const session = await reverseDemoService.createSession(targetStoreUrl)
			const normalizedStoreUrl = targetStoreUrl
			tokenManager.setTokensFromSession(session.tokens, normalizedStoreUrl)
			tokenManager.stopRefresh()

			setOrganizationId(session.organizationId)
			setActiveStoreUrl(normalizedStoreUrl)
			setActiveStoreSlug(session.storeSlug)
			setExternalSessionId(session.externalSessionId)
			sessionStorage.setItem(ACTIVE_STORE_URL_KEY, normalizedStoreUrl)
			sessionStorage.setItem(ACTIVE_STORE_SLUG_KEY, session.storeSlug)
			sessionStorage.setItem(ACTIVE_EXTERNAL_SESSION_ID_KEY, session.externalSessionId)
			searchPostHog.identify(
				session.externalSessionId,
				searchPostHog.buildCorrelationProperties({
					organizationId: session.organizationId,
					externalSessionId: session.externalSessionId,
					storeSlug: session.storeSlug,
					storeUrl: normalizedStoreUrl,
				}),
			)
			searchPostHog.capture(
				"search_store_session_created",
				searchPostHog.buildCorrelationProperties({
					organizationId: session.organizationId,
					externalSessionId: session.externalSessionId,
					storeSlug: session.storeSlug,
					storeUrl: normalizedStoreUrl,
				}),
				session.externalSessionId,
			)
			setIsAuthenticated(true)
			setCurrentView("search")
		} catch (error) {
			const knownError = error as StoreNotBootstrappedError
			const failureProperties = {
				...searchPostHog.buildCorrelationProperties({ organizationId, storeUrl: targetStoreUrl }),
				...searchPostHog.normalizeError(error),
				bootstrap_required: Boolean(knownError?.bootstrapRequired || knownError?.code === "STORE_NOT_BOOTSTRAPPED"),
				...(knownError?.code ? { error_code: knownError.code } : {}),
			}
			searchPostHog.capture("search_store_session_failed", failureProperties)
			if (knownError?.bootstrapRequired || knownError?.code === "STORE_NOT_BOOTSTRAPPED") {
				searchPostHog.capture("search_store_bootstrap_modal_shown", {
					...searchPostHog.buildCorrelationProperties({ organizationId, storeUrl: targetStoreUrl }),
					bootstrap_profile_name: knownError?.bootstrapDefaults?.profileName || "reverse-demo",
					bootstrap_run_extractor:
						typeof knownError?.bootstrapDefaults?.runExtractor === "boolean"
							? knownError.bootstrapDefaults.runExtractor
							: true,
					bootstrap_default_currency: (knownError?.bootstrapDefaults?.defaultCurrency || "INR").toUpperCase(),
				})
				setBootstrapPendingStoreUrl(targetStoreUrl)
				setBootstrapProfileName(knownError?.bootstrapDefaults?.profileName || "reverse-demo")
				setBootstrapRunExtractor(
					typeof knownError?.bootstrapDefaults?.runExtractor === "boolean"
						? knownError.bootstrapDefaults.runExtractor
						: true,
				)
				setBootstrapDefaultCurrency((knownError?.bootstrapDefaults?.defaultCurrency || "INR").toUpperCase())
				setBootstrapModalOpen(true)
				setAuthError("This store is not bootstrapped yet. Configure options and bootstrap first.")
			} else {
				setAuthError(error instanceof Error ? error.message : "Failed to create session")
			}
		} finally {
			setIsLoading(false)
		}
	}

	const handleUseStore = async () => {
		await enterStore(storeUrl)
	}

	const handleRefreshStores = async (showError: boolean = true) => {
		if (!canOperate) {
			if (showError) {
				setAuthError("Sign in as an operator, or enter the operator key, to manage stores")
			}
			return
		}

		try {
			if (showError) {
				setAuthError(null)
			}
			const allStores = await reverseDemoService.listStores(await adminKeys.authHeaders())
			setStores(allStores)
		} catch (error) {
			if (showError) {
				setAuthError(error instanceof Error ? error.message : "Failed to load stores")
			}
		}
	}

	const handleBootstrapStore = async (inputStoreUrl?: string) => {
		const targetStoreUrl = (inputStoreUrl || bootstrapPendingStoreUrl || storeUrl).trim()
		if (!canOperate) {
			setAuthError("Sign in as an operator, or enter the operator key, to bootstrap a store")
			return
		}

		if (!targetStoreUrl) {
			setAuthError("Store URL is required for bootstrap")
			return
		}

		try {
			setIsLoading(true)
			setAuthError(null)
			searchPostHog.capture("search_store_bootstrap_confirmed", {
				...searchPostHog.buildCorrelationProperties({ organizationId, storeUrl: targetStoreUrl }),
				bootstrap_profile_name: bootstrapProfileName || "reverse-demo",
				bootstrap_default_currency: bootstrapDefaultCurrency.trim().toUpperCase(),
				bootstrap_run_extractor: bootstrapRunExtractor,
			})

			await reverseDemoService.bootstrapStore(
				{
					storeUrl: targetStoreUrl,
					profileName: bootstrapProfileName || "reverse-demo",
					defaultCurrency: bootstrapDefaultCurrency.trim().toUpperCase(),
					runExtractor: bootstrapRunExtractor,
				},
				await adminKeys.authHeaders(),
			)

			setBootstrapModalOpen(false)
			await handleRefreshStores(false)
			await enterStore(targetStoreUrl)
		} catch (error) {
			searchPostHog.capture("search_store_bootstrap_failed", {
				...searchPostHog.buildCorrelationProperties({ organizationId, storeUrl: targetStoreUrl }),
				...searchPostHog.normalizeError(error),
				bootstrap_profile_name: bootstrapProfileName || "reverse-demo",
				bootstrap_default_currency: bootstrapDefaultCurrency.trim().toUpperCase(),
				bootstrap_run_extractor: bootstrapRunExtractor,
			})
			setAuthError(error instanceof Error ? error.message : "Bootstrap failed")
		} finally {
			setIsLoading(false)
		}
	}

	// Clerk emails the merchant an invitation to administer their store's in5 organization.
	const handleInviteAdmin = async (storeSlug: string) => {
		const email = window.prompt(`Invite a store admin for ${storeSlug}. Their email:`)?.trim()
		if (!email) return
		try {
			setAuthError(null)
			await reverseDemoService.inviteAdmin(storeSlug, email, await adminKeys.authHeaders())
			setOpenActionsForStore(null)
			window.alert(`Invitation sent to ${email}. They sign in to Search Admin with that email once they accept.`)
		} catch (error) {
			setAuthError(error instanceof Error ? error.message : "Invite failed")
		}
	}

	const handleDeleteStore = async (storeSlug: string, mode: "soft" | "hard") => {
		if (!canOperate) {
			setAuthError("Sign in as an operator, or enter the operator key, to delete a store")
			return
		}

		try {
			setIsLoading(true)
			setAuthError(null)
			await reverseDemoService.deleteStore(storeSlug, mode, await adminKeys.authHeaders(), true)
			setOpenActionsForStore(null)
			await handleRefreshStores(false)
		} catch (error) {
			setAuthError(error instanceof Error ? error.message : "Delete failed")
		} finally {
			setIsLoading(false)
		}
	}

	const handleRetry = () => {
		setAuthError(null)
		enterStore(storeUrl)
	}

	const handleSignOut = () => {
		if (activeStoreUrl || activeStoreSlug || externalSessionId) {
			searchPostHog.capture("search_store_switched", buildActiveCorrelationProperties(), externalSessionId || undefined)
		}
		tokenManager.clearToken()
		setIsAuthenticated(false)
		setCurrentView("search")
		setAuthError(null)
		setOrganizationId(import.meta.env.VITE_ORGANIZATION_ID || "default-org")
		setActiveStoreUrl("")
		setActiveStoreSlug("")
		setExternalSessionId("")
		sessionStorage.removeItem(ACTIVE_STORE_URL_KEY)
		sessionStorage.removeItem(ACTIVE_STORE_SLUG_KEY)
		sessionStorage.removeItem(ACTIVE_EXTERNAL_SESSION_ID_KEY)
		searchPostHog.reset()
	}

	// Loading state
	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-background">
				<div className="text-center space-y-4">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
					<p className="text-muted-foreground">Connecting to search service...</p>
				</div>
			</div>
		)
	}

	if (!isAuthenticated) {
		return (
			<div className="min-h-screen bg-background text-foreground">
				<div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
					<div>
						<h1 className="text-2xl font-bold">in5 Reverse Demo Search</h1>
						<p className="text-sm text-muted-foreground mt-1">
							Use a store URL to create a search session, or manage store bootstrap lifecycle.
						</p>
					</div>

					<AdminKeysPanel kinds={["operator"]} />

					<div className="border rounded-lg p-4 space-y-3">
						<h2 className="font-semibold">New Store</h2>
						<input
							className="w-full border rounded px-3 py-2 bg-background"
							onChange={(e) => setStoreUrl(e.target.value)}
							placeholder="https://examplestore.com"
							value={storeUrl}
						/>
						<div className="flex gap-2">
							<button className="px-4 py-2 bg-primary text-primary-foreground rounded" onClick={handleUseStore}>
								Enter Store
							</button>
							<button className="px-4 py-2 border rounded" onClick={handleRetry}>
								Retry
							</button>
						</div>
					</div>

					{enableAdmin && (
						<div className="border rounded-lg p-4 space-y-4">
							<div className="flex items-center justify-between">
								<h2 className="font-semibold">Stores</h2>
								<button className="px-3 py-1.5 border rounded text-sm" onClick={() => handleRefreshStores(true)}>
									Reload
								</button>
							</div>

							<div className="flex flex-wrap gap-2">
								<select
									className="border rounded px-3 py-2 bg-background"
									onChange={(e) => setBootstrapProfileName(e.target.value)}
									value={bootstrapProfileName}>
									<option value="reverse-demo">reverse-demo</option>
								</select>
								<label className="inline-flex items-center gap-2 border rounded px-3 py-2">
									<input
										checked={bootstrapRunExtractor}
										onChange={(e) => setBootstrapRunExtractor(e.target.checked)}
										type="checkbox"
									/>
									<span className="text-sm">Run Extractor</span>
								</label>
								<select
									className="border rounded px-3 py-2 bg-background"
									onChange={(e) => setBootstrapDefaultCurrency(e.target.value)}
									value={bootstrapDefaultCurrency}>
									{CURRENCY_OPTIONS.map((currency) => (
										<option key={currency.code} value={currency.code}>
											{currency.code} ({currency.symbol}) - {currency.label}
										</option>
									))}
								</select>
							</div>

							<div className="text-xs text-muted-foreground">
								These are default bootstrap options used when a store needs first-time provisioning.
							</div>

							<div className="max-h-72 overflow-auto border rounded">
								<table className="w-full text-sm">
									<thead className="bg-muted/40">
										<tr>
											<th className="text-left p-2">Store</th>
											<th className="text-left p-2">Org</th>
											<th className="text-left p-2">Collections</th>
											<th className="text-right p-2">Actions</th>
										</tr>
									</thead>
									<tbody>
										{stores.map((s) => (
											<tr className="border-t" key={s.storeSlug}>
												<td className="p-2">
													<button
														className="font-medium text-left hover:underline"
														onClick={() => enterStore(s.storeUrl)}>
														{s.storeSlug}
													</button>
													<a
														className="text-xs text-muted-foreground hover:underline"
														href={s.storeUrl}
														rel="noreferrer"
														target="_blank">
														{s.storeUrl}
													</a>
												</td>
												<td className="p-2 font-mono text-xs">{s.organizationId}</td>
												<td className="p-2">{(s.selectedCollections || []).join(", ")}</td>
												<td className="p-2 text-right">
													<div className="relative inline-block">
														<button
															className="px-2 py-1 border rounded"
															onClick={() =>
																setOpenActionsForStore(
																	openActionsForStore === s.storeSlug ? null : s.storeSlug,
																)
															}
															title="Store actions">
															⋯
														</button>
														{openActionsForStore === s.storeSlug && (
															<div className="absolute right-0 mt-1 z-10 bg-background border rounded shadow min-w-32">
																{adminSession.operator && (
																	<button
																		className="w-full text-left px-3 py-2 hover:bg-accent"
																		onClick={() => handleInviteAdmin(s.storeSlug)}>
																		Invite admin
																	</button>
																)}
																<button
																	className="w-full text-left px-3 py-2 hover:bg-accent"
																	onClick={() => handleDeleteStore(s.storeSlug, "soft")}>
																	Soft delete
																</button>
																<button
																	className="w-full text-left px-3 py-2 hover:bg-accent text-destructive"
																	onClick={() => handleDeleteStore(s.storeSlug, "hard")}>
																	Hard delete
																</button>
															</div>
														)}
													</div>
												</td>
											</tr>
										))}
										{stores.length === 0 && (
											<tr>
												<td className="p-3 text-muted-foreground" colSpan={4}>
													No stores found.
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					)}

					{authError && <div className="text-sm text-destructive">{authError}</div>}

					{bootstrapModalOpen && (
						<div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
							<div className="w-full max-w-lg bg-background border rounded-lg p-5 space-y-4">
								<h3 className="text-lg font-semibold">Store not bootstrapped</h3>
								<p className="text-sm text-muted-foreground">
									Bootstrap this store first? You can adjust defaults before continuing.
								</p>
								<div className="text-sm border rounded p-3 bg-muted/20">{bootstrapPendingStoreUrl}</div>
								<div className="flex flex-wrap gap-2">
									<select
										className="border rounded px-3 py-2 bg-background"
										onChange={(e) => setBootstrapProfileName(e.target.value)}
										value={bootstrapProfileName}>
										<option value="reverse-demo">reverse-demo</option>
									</select>
									<label className="inline-flex items-center gap-2 border rounded px-3 py-2">
										<input
											checked={bootstrapRunExtractor}
											onChange={(e) => setBootstrapRunExtractor(e.target.checked)}
											type="checkbox"
										/>
										<span className="text-sm">Run Extractor</span>
									</label>
									<select
										className="border rounded px-3 py-2 bg-background"
										onChange={(e) => setBootstrapDefaultCurrency(e.target.value)}
										value={bootstrapDefaultCurrency}>
										{CURRENCY_OPTIONS.map((currency) => (
											<option key={currency.code} value={currency.code}>
												{currency.code} ({currency.symbol}) - {currency.label}
											</option>
										))}
									</select>
								</div>
								<div className="flex justify-end gap-2">
									<button
										className="px-4 py-2 border rounded"
										onClick={() => {
											setBootstrapModalOpen(false)
											setBootstrapPendingStoreUrl("")
										}}>
										No
									</button>
									<button
										className="px-4 py-2 bg-primary text-primary-foreground rounded"
										onClick={() => handleBootstrapStore(bootstrapPendingStoreUrl)}>
										Yes, Bootstrap
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		)
	}

	// Authenticated - show search interface
	return (
		<SearchProvider
			organizationId={organizationId}
			reverseDemoExternalSessionId={externalSessionId}
			reverseDemoStoreSlug={activeStoreSlug}
			reverseDemoStoreUrl={activeStoreUrl}>
			<div className="min-h-screen bg-background">
				{/* Header */}
				<header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
					<div className="container mx-auto px-4 py-4">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<div className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
									in5 Search
								</div>
								<span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
									Beta
								</span>
							</div>
							<div className="flex items-center gap-4">
								<button
									className="px-3 py-1.5 rounded-md border border-border hover:bg-accent text-sm transition-colors"
									onClick={handleSignOut}
									title="Switch to another store">
									Switch Store
								</button>
								<button
									className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
									onClick={() => {
										if (currentView === "search") {
											searchPostHog.capture(
												"search_settings_opened",
												buildActiveCorrelationProperties(),
												externalSessionId || undefined,
											)
										}
										setCurrentView(currentView === "search" ? "settings" : "search")
									}}
									title="Settings">
									<Settings className="w-5 h-5" />
								</button>
								<button
									className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
									onClick={handleSignOut}
									title="Sign out">
									<LogOut className="w-5 h-5" />
								</button>
								<div className="h-6 w-px bg-border mx-2" />
								<div className="flex items-center gap-2">
									<div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
									<span className="text-xs text-muted-foreground">Connected</span>
								</div>
							</div>
						</div>
					</div>
				</header>

				{/* Main Content */}
				<main className="h-[calc(100vh-73px)]">
					{currentView === "search" ? (
						<SearchPage
							onProductClick={(product) => {
								console.log("[App] Product clicked:", product)
								searchPostHog.capture(
									"search_product_clicked",
									{
										...buildActiveCorrelationProperties(),
										...searchPostHog.buildSafeProductProperties(product),
									},
									externalSessionId || undefined,
								)
								if (product.url) {
									window.open(product.url, "_blank", "noopener,noreferrer")
								}
							}}
							organizationId={organizationId}
						/>
					) : (
						<SettingsPage
							onBack={() => setCurrentView("search")}
							organizationId={organizationId}
							storeUrl={activeStoreUrl}
						/>
					)}
				</main>
			</div>
		</SearchProvider>
	)
}
